/** @format */

import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { MvnoOperationSetupCollectionInterface } from '../../../interfaces/platform/MvnoOperatorSetup';
import { PlatformResellerMvnoOperationSetupCollectionInterface } from '../../../interfaces/platform/PlatformResellerMvnoOperationSetup';
import SimCardInterface from '../../../interfaces/platform/SimCardInterface';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import { SimpleSelect } from '../../../components/Adescom/SimpleSelect';
import { ProductActions } from '../../../modules/simcard/ProductReducer';

type ProductChangeProfileChooseType = {
    product: SimCardInterface;
    open: boolean;
    close: () => void;
    reload: () => void;
};

export const ProductChangeProfileChoose: FunctionComponent<ProductChangeProfileChooseType> = ({ product, open, close, reload }): ReactElement => {
    const dispatch = useDispatch();
    const [mvnoOperatorSetups, setMvnoOperatorSetups] = useState<MvnoOperationSetupCollectionInterface | null>(null);
    const [
        platformResellerMvnoOperationSetup,
        setPlatformResellerMvnoOperationSetup,
    ] = useState<PlatformResellerMvnoOperationSetupCollectionInterface | null>(null);
    const [profiles, setProfiles] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [profileId, setProfileId] = useState(-1);
    const [simCardId, setSimCardId] = useState(-1);

    useEffect(() => {
        if (product.id !== simCardId) {
            setMvnoOperatorSetups(null);
            setPlatformResellerMvnoOperationSetup(null);
            setProfiles(null);
            setProfileId(-1);
            setSimCardId(product.id);
        }
    }, [product, simCardId]);

    useEffect(() => {
        if (!open) {
            setMvnoOperatorSetups(null);
            setPlatformResellerMvnoOperationSetup(null);
            setProfiles(null);
            setProfileId(-1);
        }
    }, [open, product]);

    useEffect(() => {
        if (open && mvnoOperatorSetups === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/mvno_operator_setups')),
                'MVNO_OPERATOR_SETUP_GET_FOR_SIM_CARD',
                [
                    { value: 'TARIFF', name: 'typeValue' },
                    { name: 'operatorType', value: 2 },
                ],
                [{ name: 'displayName', value: 'asc' }],
                (data: MvnoOperationSetupCollectionInterface): void => {
                    setMvnoOperatorSetups(data);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [open, mvnoOperatorSetups, dispatch]);

    useEffect(() => {
        if (platformResellerMvnoOperationSetup === null && open) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_reseller_mvno_operator_setups')),
                'SIM_CARD_ALLOWED_PROFILE',
                [
                    { value: product.client.platformResellerId, name: 'platformReseller' },
                    { name: 'statusFlag[lt]', value: 10 },
                ],
                [],
                (data: PlatformResellerMvnoOperationSetupCollectionInterface): void => {
                    setPlatformResellerMvnoOperationSetup(data);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [dispatch, platformResellerMvnoOperationSetup, product, open]);

    useEffect(() => {
        if (profiles === null && mvnoOperatorSetups && platformResellerMvnoOperationSetup) {
            const list: Array<HeaderCheckBoxSelectListElement> = [];
            for (let i = 0; i < platformResellerMvnoOperationSetup['hydra:member'].length; i++) {
                for (let j = 0; j < mvnoOperatorSetups['hydra:member'].length; j++) {
                    if (
                        platformResellerMvnoOperationSetup['hydra:member'][i].mvnoOperatorSetup.replace(/base-api/, 'adescom-api') ===
                            mvnoOperatorSetups['hydra:member'][j]['@id'] &&
                        platformResellerMvnoOperationSetup['hydra:member'][i].statusFlag === 1 &&
                        (product.mvnoTariffId ?? -1).toString() !== mvnoOperatorSetups['hydra:member'][j].constValue
                    ) {
                        list.push({
                            name: mvnoOperatorSetups['hydra:member'][j].displayName,
                            value: parseInt(mvnoOperatorSetups['hydra:member'][j].constValue),
                        });
                    }
                }
            }
            setProfiles(list);
        }
    }, [profiles, mvnoOperatorSetups, platformResellerMvnoOperationSetup, product]);

    const save = (): void => {
        if (profileId === null) {
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        const params = {
            activationType: 8,
            id: product.id,
            params: JSON.stringify({
                profileChangeTo: profileId,
            }),
        };
        itemLoader.putWithCallback(
            FormGeneralHelper.getUrl('/operation_on_sim_cards/' + product.id),
            'operation_on_sim_cards',
            params,
            (response: any) => {
                dispatch({
                    type: ProductActions.PRODUCT_REFRESH_VIEW,
                    uri: product['@id'],
                });
                close();
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const onChangeSelectEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const field = event.target.name;
        const selected = parseInt(event.target.value);
        setProfileId(selected);
    };

    return (
        <Dialog open={open} onClose={close} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                <I18n trans="simcard:v.profile_choose" />
            </DialogTitle>
            <DialogContent>
                <SimpleSelect
                    label="simcard:v.profile_choose"
                    fieldName="useType"
                    required={true}
                    elements={profiles ?? []}
                    dispatchName={'profile_id'}
                    selectedValue={profileId}
                    callback={onChangeSelectEvent}
                />
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={close} callbackParam={{}} label="general:common.close" size="sm" />
                <CustomTextButtom callback={save} callbackParam={{}} label="general:common.save" size="sm" />
            </DialogActions>
        </Dialog>
    );
};
